import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { playDrop, playReactionFizz, setMuted, isMuted } from "@/lib/labSounds";
import { type LabChemical, labChemicals, findReaction, explainNoReaction, getRequiredTemperature, type Reaction, type NoReactionExplanation } from "@/data/reactions";
import AppHeader from "@/components/AppHeader";
import ChemicalShelf from "@/components/lab/ChemicalShelf";
import MixingBeaker from "@/components/lab/MixingBeaker";
import ReactionResult from "@/components/lab/ReactionResult";
import FlameTest from "@/components/lab/FlameTest";
import PHTest from "@/components/lab/PHTest";
import LabSettings, { type LabUnits, defaultLabUnits } from "@/components/lab/LabSettings";
import LabNotebook from "@/components/lab/LabNotebook";
import LabControls from "@/components/lab/LabControls";
import NoReactionExplainer from "@/components/lab/NoReactionExplainer";
import EquationPrediction from "@/components/lab/EquationPrediction";
import { useLabNotebook } from "@/lib/labNotebook";
import { useTablistKeyboard } from "@/hooks/useTablistKeyboard";
import { useLiveRegion } from "@/hooks/useLiveRegion";
import { motion, AnimatePresence } from "framer-motion";
import { FlaskConical, RotateCcw, NotebookPen, Flame, Droplets, Beaker, Volume2, VolumeX, Settings, Notebook, Undo2, Redo2, Thermometer, Shuffle, Clock, AlertTriangle, Eye, Share2, ChevronDown } from "@/lib/icons";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { suggestedExperiments, type SuggestedExperiment, type ExperimentDifficulty } from "@/data/suggestedExperiments";
import Main from "@/components/Main";
import Seo from "@/components/Seo";

// Detect Mac so we can show ⌘ vs Ctrl in shortcut hints.
const isMac = typeof navigator !== "undefined" && /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent || "");
const MOD_KEY = isMac ? "⌘" : "Ctrl";

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded border border-border bg-muted text-[10px] font-mono font-medium text-foreground">
      {children}
    </kbd>
  );
}

const DIFFICULTY_STYLES: Record<ExperimentDifficulty, { label: string; className: string }> = {
  beginner:     { label: "Beginner",     className: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" },
  intermediate: { label: "Intermediate", className: "bg-sky-500/10 text-sky-500 border-sky-500/30" },
  advanced:     { label: "Advanced",     className: "bg-orange-500/10 text-orange-500 border-orange-500/30" },
};

function ExperimentCard({
  exp,
  onLoad,
}: {
  exp: SuggestedExperiment;
  onLoad: () => void;
}) {
  const diff = DIFFICULTY_STYLES[exp.difficulty];
  return (
    <button
      role="listitem"
      onClick={onLoad}
      aria-label={`Load ${exp.label} experiment. Difficulty ${diff.label}, takes about ${exp.estimatedMinutes} minutes.`}
      className="flex flex-col gap-2 p-3 rounded-lg bg-muted/40 border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-left group cursor-pointer flex-shrink-0 w-[78%] sm:w-auto snap-start"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-foreground group-hover:text-primary transition-colors leading-tight">
            {exp.label}
          </div>
          <div className="text-[11px] font-mono text-muted-foreground mt-0.5 truncate">{exp.hint}</div>
        </div>
        <span className={`shrink-0 text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded border ${diff.className}`}>
          {diff.label}
        </span>
      </div>
      <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground leading-snug">
        <Eye size={11} className="mt-0.5 shrink-0 opacity-70" aria-hidden="true" />
        <span className="line-clamp-2">{exp.observation}</span>
      </div>
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Clock size={10} aria-hidden="true" />
          ~{exp.estimatedMinutes} min
        </span>
        {exp.safetyNote && (
          <span
            className="inline-flex items-center gap-1 text-destructive"
            title={exp.safetyNote}
          >
            <AlertTriangle size={10} aria-hidden="true" />
            <span className="truncate max-w-[140px]">{exp.safetyNote}</span>
          </span>
        )}
      </div>
    </button>
  );
}

type LabTab = "mixing" | "flame" | "ph" | "notebook" | "settings";

const tabs: { key: LabTab; label: string; icon: React.ReactNode }[] = [
  { key: "mixing", label: "Mixing Station", icon: <Beaker size={14} /> },
  { key: "flame", label: "Flame Test", icon: <Flame size={14} /> },
  { key: "ph", label: "pH Testing", icon: <Droplets size={14} /> },
  { key: "notebook", label: "Notebook", icon: <Notebook size={14} /> },
  { key: "settings", label: "Settings", icon: <Settings size={14} /> },
];

export default function OnlineLab() {
  const [activeTab, setActiveTab] = useState<LabTab>("mixing");
  const tabKeyDown = useTablistKeyboard<LabTab>(
    tabs.map((t) => t.key) as LabTab[],
    setActiveTab,
  );
  // History stack for undo/redo. Each entry is the full beaker state.
  const [history, setHistory] = useState<LabChemical[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const beakerChemicals = history[historyIndex] ?? [];
  const [reaction, setReaction] = useState<Reaction | null>(null);
  const [noReaction, setNoReaction] = useState<NoReactionExplanation | null>(null);
  const [isReacting, setIsReacting] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [labUnits, setLabUnits] = useState<LabUnits>(defaultLabUnits);
  const [muted, setMutedState] = useState(isMuted());
  const [concentration, setConcentration] = useState(0.5);
  const [temperature, setTemperature] = useState(25);
  const { addEntry } = useLabNotebook();
  const [loggedReactionIds, setLoggedReactionIds] = useState<Set<string>>(new Set());
  // "More popular school experiments" rail is collapsed by default to reduce visual noise.
  const [showMoreExperiments, setShowMoreExperiments] = useState(false);
  const { message: liveMessage, politeness, announce } = useLiveRegion();
  // Track whether the temperature threshold for the current beaker contents
  // was previously met, so we can announce only on the rising edge.
  const thresholdMetRef = useRef(false);
  // Track previous beaker count so we can announce "first chemical added" and
  // "beaker is now empty" transitions regardless of how the change happened
  // (drag/click, undo, redo, reset, shared link, suggested experiment).
  const prevBeakerCountRef = useRef(beakerChemicals.length);
  useEffect(() => {
    const prev = prevBeakerCountRef.current;
    const curr = beakerChemicals.length;
    if (prev === 0 && curr > 0) {
      const first = beakerChemicals[0];
      announce(
        `First chemical added. Beaker now contains ${first.name}.`,
        "assertive",
      );
    } else if (prev > 0 && curr === 0) {
      announce("Beaker is now empty.", "assertive");
    }
    prevBeakerCountRef.current = curr;
  }, [beakerChemicals, announce]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  /** Push a new beaker state onto the history stack, truncating any redo branch. */
  const pushHistory = useCallback(
    (next: LabChemical[]) => {
      setHistory((prev) => {
        const truncated = prev.slice(0, historyIndex + 1);
        return [...truncated, next];
      });
      setHistoryIndex((i) => i + 1);
    },
    [historyIndex],
  );

  const toggleMute = useCallback(() => {
    const next = !muted;
    setMutedState(next);
    setMuted(next);
  }, [muted]);

  const addChemical = useCallback(
    (chem: LabChemical) => {
      if (beakerChemicals.length >= 4) return;
      playDrop();
      const next = [...beakerChemicals, chem];
      pushHistory(next);
      // The empty→non-empty transition is announced by the dedicated
      // beaker-count effect ("First chemical added…"), so only announce
      // subsequent additions here to avoid double-speak.
      if (beakerChemicals.length > 0) {
        announce(`${chem.name} added to the beaker.`);
      }
    },
    [beakerChemicals, pushHistory, announce],
  );

  const removeChemical = useCallback(
    (index: number) => {
      const removed = beakerChemicals[index];
      const next = beakerChemicals.filter((_, i) => i !== index);
      pushHistory(next);
      // The non-empty→empty transition is announced by the dedicated
      // beaker-count effect ("Beaker is now empty."), so only announce
      // partial removals here to avoid double-speak.
      if (removed && next.length > 0) {
        announce(`${removed.name} removed from the beaker.`);
      }
    },
    [beakerChemicals, pushHistory, announce],
  );

  const reset = useCallback(() => {
    setHistory([[]]);
    setHistoryIndex(0);
    setLoggedReactionIds(new Set());
    thresholdMetRef.current = false;
    announce("Beaker reset. Ready for a new experiment.");
  }, [announce]);

  const undo = useCallback(() => {
    if (!canUndo) return;
    setHistoryIndex((i) => i - 1);
    announce("Undid last action.");
  }, [canUndo, announce]);

  const redo = useCallback(() => {
    if (!canRedo) return;
    setHistoryIndex((i) => i + 1);
    announce("Redid action.");
  }, [canRedo, announce]);

  // Re-evaluate the reaction whenever beaker contents OR temperature change.
  useEffect(() => {
    const formulas = beakerChemicals.map((c) => c.formula);
    const required = getRequiredTemperature(formulas);
    const isGated = required !== null && required > 25;
    const isMet = required !== null && temperature >= required;

    // If the beaker no longer has a heat-gated combo, clear the ref so a
    // future experiment will announce again.
    if (!isGated) thresholdMetRef.current = false;

    // Detect rising-edge crossing: was below required, now at/above it.
    if (isGated && isMet && !thresholdMetRef.current) {
      toast.success(`Hot plate at ${temperature} °C - reaction enabled.`, {
        description: `Threshold of ${required} °C reached.`,
        duration: 4000,
      });
      announce(
        `Temperature threshold reached at ${temperature} degrees Celsius. Reaction starting.`,
        "assertive",
      );
    }
    thresholdMetRef.current = isMet;

    const found = findReaction(formulas, temperature);
    if (found) {
      setNoReaction(null);
      setReaction(found);
      setIsReacting(true);
      const t = window.setTimeout(() => {
        playReactionFizz();
        setShowResult(true);
        announce(
          `Reaction: ${found.equation}. ${found.observation}. ${found.energy === "exothermic" ? "Exothermic" : "Endothermic"} ${found.typeLabel.toLowerCase()} reaction.`,
          "assertive",
        );
        setLoggedReactionIds((logged) => {
          if (logged.has(found.id)) return logged;
          addEntry({
            station: "mixing",
            title: found.equation,
            reactants: found.reactants,
            products: found.products,
            equation: found.equation,
            reactionType: found.typeLabel,
            energy: found.energy,
            observation: found.observation,
          });
          // Surface auto-logging so students notice the notebook.
          toast.success("Saved to notebook", {
            description: found.equation,
            duration: 3000,
          });
          const nextLogged = new Set(logged);
          nextLogged.add(found.id);
          return nextLogged;
        });
      }, 1200);
      return () => window.clearTimeout(t);
    }
    // No reaction: reset reaction state and surface explainer when ≥1 chemical present.
    setReaction(null);
    setIsReacting(false);
    setShowResult(false);
    setNoReaction(beakerChemicals.length > 0 ? explainNoReaction(formulas, temperature) : null);
  }, [beakerChemicals, temperature, addEntry, announce]);

  // Keyboard shortcuts: Ctrl/Cmd+Z (undo), Ctrl/Cmd+Shift+Z or Ctrl+Y (redo)
  useEffect(() => {
    if (activeTab !== "mixing") return;
    const onKey = (e: KeyboardEvent) => {
      const meta = e.ctrlKey || e.metaKey;
      if (!meta) return;
      // Don't hijack typing inside inputs/textareas.
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      if (e.key.toLowerCase() === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key.toLowerCase() === "z" && e.shiftKey) || e.key.toLowerCase() === "y") {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeTab, undo, redo]);

  const loadExperiment = useCallback(
    (formulas: string[]) => {
      const chems = formulas
        .map((f) => labChemicals.find((c) => c.formula === f))
        .filter(Boolean) as LabChemical[];
      if (chems.length === 0) return;
      // Reset history and load all chemicals in a single new state to avoid
      // stale-closure issues when chaining multiple addChemical() calls.
      setHistory([[], chems.slice(0, 4)]);
      setHistoryIndex(1);
      setLoggedReactionIds(new Set());
      thresholdMetRef.current = false;
      playDrop();
      announce(
        `Loaded experiment with ${chems.map((c) => c.name).join(", ")}.`,
      );
    },
    [announce],
  );

  // ----- Shareable experiment URL -----
  // ?mix=Na,H2O&temp=25&conc=0.5  (subscript-stripped formulas)
  const sharedHydrationRef = useRef(false);
  useEffect(() => {
    if (sharedHydrationRef.current) return;
    sharedHydrationRef.current = true;
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const mix = params.get("mix");
    if (!mix) return;
    const asciiToFormula = (a: string) =>
      a.replace(/(\d)/g, (d) => ({ "2": "₂", "3": "₃", "4": "₄" }[d] || d));
    const formulas = mix.split(",").map(asciiToFormula).filter(Boolean);
    if (formulas.length) loadExperiment(formulas);
    const t = params.get("temp");
    if (t && !Number.isNaN(parseInt(t))) setTemperature(parseInt(t));
    const c = params.get("conc");
    if (c && !Number.isNaN(parseFloat(c))) setConcentration(parseFloat(c));
  }, [loadExperiment]);

  const shareExperiment = useCallback(async () => {
    if (beakerChemicals.length === 0) {
      toast("Add some chemicals first", { description: "Then share your experiment with classmates." });
      return;
    }
    const formulaToAscii = (f: string) =>
      f.replace(/[₂₃₄]/g, (c) => ({ "₂": "2", "₃": "3", "₄": "4" }[c] || c));
    const params = new URLSearchParams();
    params.set("mix", beakerChemicals.map((c) => formulaToAscii(c.formula)).join(","));
    if (temperature !== 25) params.set("temp", String(temperature));
    if (concentration !== 0.5) params.set("conc", String(concentration));
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied", { description: url, duration: 4000 });
    } catch {
      toast.error("Copy failed", { description: "Copy this URL manually: " + url });
    }
  }, [beakerChemicals, temperature, concentration]);

  return (
    <div className="min-h-screen bg-transparent">
      <Seo title="Online Chemistry Lab — ElementPad" description="Mix reagents, run titrations, flame tests, gas laws, and calorimetry in a safe virtual chemistry lab." path="/lab" />
      <AppHeader />

      <Main className="max-w-[1200px] mx-auto px-4 py-6">
        {/* Screen reader live region for lab announcements */}
        <div
          role="status"
          aria-live={politeness}
          aria-atomic="true"
          className="sr-only"
        >
          {liveMessage}
        </div>

        {/* Title */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10 border border-primary/20" aria-hidden="true">
              <FlaskConical size={20} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Online Lab</h1>
              <p className="text-sm text-muted-foreground">Mix chemicals, test flames, and measure pH</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleMute}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-accent border border-border transition-colors"
              aria-label={muted ? "Unmute lab sounds" : "Mute lab sounds"}
              aria-pressed={muted}
            >
              {muted ? <VolumeX size={12} aria-hidden="true" /> : <Volume2 size={12} aria-hidden="true" />}
              {muted ? "Muted" : "Sound"}
            </button>

            {activeTab === "mixing" && (
              <>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={undo}
                        disabled={!canUndo}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-accent border border-border transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                        aria-label={`Undo last action. Shortcut: ${MOD_KEY}+Z`}
                        aria-keyshortcuts={isMac ? "Meta+Z" : "Control+Z"}
                      >
                        <Undo2 size={12} aria-hidden="true" />
                        Undo
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="flex items-center gap-2 text-xs">
                      <span>Undo</span>
                      <span className="flex items-center gap-1">
                        <Kbd>{MOD_KEY}</Kbd>
                        <span className="text-muted-foreground">+</span>
                        <Kbd>Z</Kbd>
                      </span>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={redo}
                        disabled={!canRedo}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-accent border border-border transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                        aria-label={`Redo action. Shortcut: ${MOD_KEY}+Shift+Z`}
                        aria-keyshortcuts={isMac ? "Meta+Shift+Z" : "Control+Shift+Z"}
                      >
                        <Redo2 size={12} aria-hidden="true" />
                        Redo
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="flex items-center gap-2 text-xs">
                      <span>Redo</span>
                      <span className="flex items-center gap-1">
                        <Kbd>{MOD_KEY}</Kbd>
                        <span className="text-muted-foreground">+</span>
                        <Kbd>⇧</Kbd>
                        <span className="text-muted-foreground">+</span>
                        <Kbd>Z</Kbd>
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              <button
                onClick={reset}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-accent border border-border transition-colors"
                aria-label="Reset experiment"
              >
                <RotateCcw size={12} aria-hidden="true" />
                Reset
              </button>
              <button
                onClick={shareExperiment}
                disabled={beakerChemicals.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-accent border border-border transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                aria-label="Copy a shareable link to this experiment"
              >
                <Share2 size={12} aria-hidden="true" />
                Share
              </button>
              </>
            )}
          </div>
        </div>

        {/* Tab navigation */}
        <div className="flex items-center gap-1 mb-6 p-1 rounded-lg bg-muted/50 border border-border w-fit" role="tablist" aria-label="Lab stations">
          {tabs.map((tab, i) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                onKeyDown={tabKeyDown(i)}
                role="tab"
                aria-selected={isActive}
                aria-controls={`tabpanel-${tab.key}`}
                id={`tab-${tab.key}`}
                tabIndex={isActive ? 0 : -1}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  isActive
                    ? "bg-background text-foreground shadow-sm border border-border"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <span aria-hidden="true">{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          {activeTab === "mixing" && (
            <motion.div
              key="mixing"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
                <div className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <NotebookPen size={14} className="text-primary" />
                    <h2 className="text-sm font-medium text-foreground">Chemical Shelf</h2>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-3">
                    Click or drag chemicals into the beaker.
                  </p>
                  <div className="h-[500px] lg:h-[600px]">
                    <ChemicalShelf onAddChemical={addChemical} />
                  </div>
                </div>

                <div className="flex flex-col items-center gap-6">
                  <div className="w-full rounded-xl border border-border bg-gradient-to-b from-card to-muted/30 p-6 min-h-[400px] flex flex-col items-center justify-center relative overflow-hidden">
                    <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-muted/50 to-transparent" />
                    <div className="absolute inset-x-0 bottom-16 h-px bg-border/50" />

                    {/* Temperature status chip (top-right of beaker container) */}
                    {beakerChemicals.length > 0 && (() => {
                      const required = getRequiredTemperature(beakerChemicals.map((c) => c.formula));
                      const hasGate = required !== null && required > 25;
                      const isMet = required !== null && temperature >= required;
                      const isHot = temperature > 100;
                      return (
                        <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5 z-10" aria-live="polite">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${
                              isHot
                                ? "bg-orange-500/10 text-orange-500 border-orange-500/30"
                                : "bg-muted text-muted-foreground border-border"
                            }`}
                            aria-label={`Beaker temperature: ${temperature} degrees Celsius`}
                          >
                            <Thermometer size={11} aria-hidden="true" />
                            <span className="font-mono tabular-nums">{temperature} °C</span>
                          </span>
                          {hasGate && !isMet && (
                            <span
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border bg-yellow-500/10 text-yellow-500 border-yellow-500/30"
                              aria-label={`This combination needs at least ${required} degrees Celsius. Raise the hot plate to start the reaction.`}
                              role="status"
                            >
                              <Flame size={11} aria-hidden="true" />
                              Needs ≥ <span className="font-mono tabular-nums">{required} °C</span>
                            </span>
                          )}
                          {hasGate && isMet && (
                            <span
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border bg-primary/10 text-primary border-primary/30"
                              aria-label="Hot plate temperature is sufficient for the reaction."
                            >
                              <Flame size={11} aria-hidden="true" />
                              Threshold met
                            </span>
                          )}
                        </div>
                      );
                    })()}

                    <MixingBeaker
                      chemicals={beakerChemicals}
                      onRemoveChemical={removeChemical}
                      reaction={reaction}
                      isReacting={isReacting}
                      onDrop={addChemical}
                      concentration={concentration}
                      temperature={temperature}
                    />

                    {beakerChemicals.length === 0 && (
                      <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-sm text-muted-foreground mt-4 text-center"
                      >
                        Add chemicals from the shelf to start experimenting
                      </motion.p>
                    )}
                  </div>

                  {/* Concentration & temperature controls */}
                  <LabControls
                    concentration={concentration}
                    onConcentrationChange={setConcentration}
                    temperature={temperature}
                    onTemperatureChange={setTemperature}
                    disabled={isReacting && !showResult}
                  />

                  <AnimatePresence>
                    {reaction && <EquationPrediction key={`pred-${reaction.id}`} reaction={reaction} showResult={showResult} />}
                    {showResult && reaction && <ReactionResult reaction={reaction} />}
                    {!reaction && noReaction && (
                      <NoReactionExplainer key="no-reaction" explanation={noReaction} />
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Suggested experiments */}
              <div className="mt-8 rounded-xl border border-border bg-card p-5">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <h3 className="text-sm font-medium text-foreground">Try These Experiments</h3>
                  <button
                    type="button"
                    onClick={() => {
                      const pick = suggestedExperiments[Math.floor(Math.random() * suggestedExperiments.length)];
                      loadExperiment(pick.formulas);
                      toast(`🎲 ${pick.label}`, { description: pick.observation });
                    }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border border-border bg-background hover:border-primary/40 hover:text-primary transition-colors"
                    aria-label="Load a random experiment"
                  >
                    <Shuffle size={12} aria-hidden="true" />
                    Random
                  </button>
                </div>
                <div
                  className="flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-3 overflow-x-auto sm:overflow-visible -mx-5 px-5 sm:mx-0 sm:px-0 snap-x snap-mandatory sm:snap-none pb-2 sm:pb-0 scrollbar-thin"
                  role="list"
                  aria-label="Suggested experiments"
                >
                  {suggestedExperiments.filter((e) => e.group === "popular").map((exp) => (
                    <ExperimentCard key={exp.label} exp={exp} onLoad={() => loadExperiment(exp.formulas)} />
                  ))}
                </div>

                {/* Secondary rail - collapsed by default to keep the lab focused */}
                <button
                  type="button"
                  onClick={() => setShowMoreExperiments((v) => !v)}
                  aria-expanded={showMoreExperiments}
                  aria-controls="more-experiments-rail"
                  className="mt-5 w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-dashed border-border text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                >
                  <span className="font-medium">
                    {showMoreExperiments ? "Hide" : "Show"} more popular school experiments
                    <span className="ml-1.5 text-muted-foreground/70">
                      ({suggestedExperiments.filter((e) => e.group === "more").length})
                    </span>
                  </span>
                  <ChevronDown
                    size={14}
                    aria-hidden="true"
                    className={`transition-transform ${showMoreExperiments ? "rotate-180" : ""}`}
                  />
                </button>
                {showMoreExperiments && (
                  <div
                    id="more-experiments-rail"
                    className="mt-3 flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-3 overflow-x-auto sm:overflow-visible -mx-5 px-5 sm:mx-0 sm:px-0 snap-x snap-mandatory sm:snap-none pb-2 sm:pb-0 scrollbar-thin"
                    role="list"
                    aria-label="More school experiments"
                  >
                    {suggestedExperiments.filter((e) => e.group === "more").map((exp) => (
                      <ExperimentCard key={exp.label} exp={exp} onLoad={() => loadExperiment(exp.formulas)} />
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground mt-2 sm:hidden">← swipe popular cards to see more →</p>
              </div>
            </motion.div>
          )}

          {activeTab === "flame" && (
            <motion.div
              key="flame"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <FlameTest />
            </motion.div>
          )}

          {activeTab === "ph" && (
            <motion.div
              key="ph"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <PHTest />
            </motion.div>
          )}

          {activeTab === "notebook" && (
            <motion.div
              key="notebook"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <LabNotebook />
            </motion.div>
          )}

          {activeTab === "settings" && (
            <motion.div
              key="settings"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <LabSettings units={labUnits} onUnitsChange={setLabUnits} />
            </motion.div>
          )}
        </AnimatePresence>
      </Main>
    </div>
  );
}
